import { useState, type ReactNode } from 'react'
import { Form, Modal, type FormInstance } from 'antd'
import { DEFAULT_CLIENT_TEXT_COLOR } from '@/features/client/clientFormShared'

type ReportFormValues = {
  primaryColor?: string
  secondaryColor?: string
  textColor?: string
}

type Colors = {
  primary: string
  secondary: string
  text: string
}

const DEFAULT_PRIMARY = '#1E40AF'
const DEFAULT_SECONDARY = '#647488'

// Fixed neutrals — the three brand colors above stay dynamic (driven by the form).
const HAIRLINE = '#DCE0E5'
const LABEL_GRAY = '#8A8A8A'
const BODY_GRAY = '#3F3F3F'
const CRITICAL_RED = '#9B1C1C'
// Off-white used for the dark closing slide.
const CREAM = '#F4F1E8'

// Editorial type system mirroring the generated deck: a high-contrast Caslon serif for
// display, Archivo for body/labels. Named faces first, web-safe fallbacks after.
const SERIF = "'Libre Caslon Text', Georgia, 'Times New Roman', Times, serif"
const SANS = "'Archivo', 'Helvetica Neue', Helvetica, Arial, sans-serif"

// 16:9 widescreen slide (PowerPoint default 13.333in x 7.5in => 960 x 540 pt).
const SLIDE_W = 960
const SLIDE_H = 540

const FALLBACK_CLIENT_NAME = 'Demo Test Client'
const FALLBACK_ORG_NAME = 'Dashboard Insights'

const SAMPLE_PERIOD = 'JUL 2024 – JUN 2025'

// Illustrative placeholder content for the preview — not tied to any real report.
const SAMPLE_METRICS: Array<{ label: string; figure: string; delta?: string; critical?: boolean }> = [
  { label: 'Revenue', figure: '$4.24M', delta: '+8.3% YoY' },
  { label: 'Gross Margin', figure: '41.5%', delta: 'up from 38.2%' },
  { label: 'Operating Cash Flow', figure: '$612K', delta: '+14% YoY' },
  { label: 'Net Profit', figure: '$487K', delta: '11.6% margin' },
  { label: 'Cash Runway', figure: '5.2 months', delta: 'Watch', critical: true },
  { label: 'Debtor Days', figure: '42 days', delta: 'down from 51' },
  { label: 'Overhead Ratio', figure: '31%', delta: 'of revenue' },
]

const SAMPLE_FINDINGS: string[] = [
  'Revenue grew 8.3% to $4.24M, led by the services line, which now contributes 46% of total income — up from 39% a year ago.',
  'Gross margin expanded 3.3 points to 41.5% on renegotiated supplier terms and a shift toward higher-value engagements.',
  'Operating cash flow of $612K outpaced net profit, reflecting tighter receivables management and a nine-day fall in debtor days.',
  'Cash runway sits at 5.2 months against current burn — workable, but thin enough to warrant a buffer before the next hiring round.',
  'Overheads climbed 12% year-on-year, ahead of revenue, with software subscriptions and contractor spend the largest contributors.',
]

const SAMPLE_RECOMMENDATIONS: Array<{ title: string; description: string }> = [
  {
    title: 'Build a Cash Buffer',
    description: 'Set aside a six-month operating reserve before new headcount, and adopt a 13-week rolling cash forecast to track runway weekly.',
  },
  {
    title: 'Formalise Pricing Reviews',
    description: 'Recent margin gains show real pricing power. Introduce an annual review and a value-based rate card for the services line.',
  },
  {
    title: 'Rein In Overheads',
    description: 'Audit software and contractor spend, consolidate overlapping tools, and hold overheads under 30% of revenue as the business scales.',
  },
  {
    title: 'Protect Recurring Revenue',
    description: 'With services near half of income, invest in account management and retention so this higher-margin stream keeps compounding.',
  },
  {
    title: 'Sharpen Reporting Cadence',
    description: 'Move to monthly management accounts with a one-page KPI dashboard so the board can act on trends well before year-end.',
  },
]

const twoDigit = (n: number) => String(n).padStart(2, '0')

// Small stacked right-aligned caption used on the summary slides.
function RunningHeader({ orgName, color }: { orgName: string; color: string }) {
  return (
    <div style={{ position: 'absolute', right: 35, top: 40, textAlign: 'right' }}>
      <div style={{ fontFamily: SANS, fontSize: 6.7, fontWeight: 700, letterSpacing: 1.2, color, textTransform: 'uppercase' }}>
        {orgName}
      </div>
      <div style={{ fontFamily: SANS, fontSize: 6.7, fontWeight: 700, letterSpacing: 1.2, color, textTransform: 'uppercase', marginTop: 3 }}>
        Business Advisory Report
      </div>
    </div>
  )
}

function Slide1({ colors, clientName, orgName }: { colors: Colors; clientName: string; orgName: string }) {
  return (
    <div
      style={{
        width: SLIDE_W,
        height: SLIDE_H,
        background: '#fff',
        fontFamily: SERIF,
        textAlign: 'left',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Full-bleed top bar */}
      <div style={{ position: 'absolute', left: 0, top: 0, width: '100%', height: 6, background: colors.primary }} />

      {/* Header: practice name (left) + period (right) */}
      <div style={{ position: 'absolute', left: 45, top: 34, fontSize: 21, fontWeight: 700, color: colors.text }}>{clientName}</div>
      <div
        style={{
          position: 'absolute',
          right: 47,
          top: 40,
          fontFamily: SANS,
          fontSize: 12,
          fontWeight: 600,
          letterSpacing: 2,
          textTransform: 'uppercase',
          color: colors.secondary,
        }}
      >
        {SAMPLE_PERIOD}
      </div>

      {/* Title block (flowed so the underline follows the title however many lines it wraps to) */}
      <div style={{ position: 'absolute', left: 45, top: 150, width: 880 }}>
        <div style={{ fontSize: 19, fontStyle: 'italic', color: colors.secondary }}>Business Advisory Report</div>
        <div style={{ fontSize: 55, fontWeight: 700, lineHeight: 1.16, color: colors.primary, marginTop: 6 }}>{orgName}</div>
        <div style={{ width: 48, height: 3, background: colors.secondary, marginTop: 14 }} />
      </div>

      {/* Footer */}
      <div style={{ position: 'absolute', left: 45, top: 474, width: 870, height: 1, background: HAIRLINE }} />
      <div
        style={{
          position: 'absolute',
          left: 45,
          top: 486,
          fontFamily: SANS,
          fontSize: 13,
          letterSpacing: 2,
          textTransform: 'uppercase',
          color: LABEL_GRAY,
        }}
      >
        Prepared using <span style={{ fontWeight: 700, color: colors.text }}>Dashboard Insights</span>
      </div>
    </div>
  )
}

function SummarySlide({
  colors,
  clientName,
  orgName,
  title,
  pageNumber,
}: {
  colors: Colors
  clientName: string
  orgName: string
  title: string
  pageNumber: string
}) {
  return (
    <div
      style={{
        width: SLIDE_W,
        height: SLIDE_H,
        background: '#fff',
        fontFamily: SANS,
        textAlign: 'left',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Full-bleed top bar */}
      <div style={{ position: 'absolute', left: 0, top: 0, width: '100%', height: 6, background: colors.primary }} />

      {/* Header: period + title (left), running caption (right), gold rule */}
      <div style={{ position: 'absolute', left: 34, top: 31, fontSize: 8, fontWeight: 600, letterSpacing: 2, textTransform: 'uppercase', color: colors.secondary }}>
        {SAMPLE_PERIOD}
      </div>
      <div style={{ position: 'absolute', left: 33, top: 44, fontFamily: SERIF, fontSize: 26, fontWeight: 700, color: colors.primary }}>
        {title}
      </div>
      <RunningHeader orgName={orgName} color={LABEL_GRAY} />
      <div style={{ position: 'absolute', left: 34, top: 92, width: 892, height: 2.5, background: colors.secondary }} />

      {/* Column 1 — Key Metrics box */}
      <div
        style={{
          position: 'absolute',
          left: 30,
          top: 104,
          width: 250,
          height: 392,
          border: `1px solid ${colors.text}`,
          boxSizing: 'border-box',
          padding: '13px 15px',
        }}
      >
        <div style={{ fontFamily: SERIF, fontSize: 17, fontWeight: 700, color: colors.text }}>Key Metrics</div>
        <div style={{ height: 1, background: colors.text, opacity: 0.5, marginTop: 7, marginBottom: 9 }} />
        {SAMPLE_METRICS.map((m, i) => (
          <div key={i} style={{ marginBottom: i === SAMPLE_METRICS.length - 1 ? 0 : 20 }}>
            <div style={{ fontSize: 7, fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase', color: LABEL_GRAY }}>
              {m.label}
            </div>
            <div style={{ fontFamily: SERIF, fontSize: 15, fontWeight: 700, lineHeight: 1.2, marginTop: 1 }}>
              <span style={{ color: m.critical ? CRITICAL_RED : colors.text }}>{m.figure}</span>
              {m.delta ? <span style={{ color: colors.secondary }}> {m.delta}</span> : null}
            </div>
          </div>
        ))}
      </div>

      {/* Column 2 — Findings */}
      <div style={{ position: 'absolute', left: 283, top: 100, width: 300 }}>
        <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: 2, color: colors.secondary, marginBottom: 12 }}>FINDINGS</div>
        {SAMPLE_FINDINGS.map((f, i) => (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: '30px 1fr', alignItems: 'start', marginBottom: 11 }}>
            <span style={{ fontFamily: SERIF, fontSize: 17, fontWeight: 700, color: colors.secondary, lineHeight: 1 }}>{twoDigit(i + 1)}</span>
            <span style={{ fontSize: 10, lineHeight: 1.5, color: BODY_GRAY }}>{f}</span>
          </div>
        ))}
      </div>

      {/* Column 3 — Recommendations */}
      <div style={{ position: 'absolute', left: 619, top: 100, width: 300 }}>
        <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: 2, color: colors.secondary, marginBottom: 12 }}>RECOMMENDATIONS</div>
        {SAMPLE_RECOMMENDATIONS.map((r, i) => (
          <div key={i} style={{ position: 'relative', paddingLeft: 12, marginBottom: 12 }}>
            <div style={{ position: 'absolute', left: 0, top: 1, bottom: 1, width: 1.5, background: colors.primary }} />
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.3, textTransform: 'uppercase', color: colors.primary }}>{r.title}</div>
            <div style={{ fontSize: 8.5, lineHeight: 1.5, color: BODY_GRAY, marginTop: 3 }}>{r.description}</div>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div style={{ position: 'absolute', left: 34, bottom: 18, fontSize: 7, letterSpacing: 1, textTransform: 'uppercase', color: LABEL_GRAY }}>
        {clientName}
      </div>
      <div style={{ position: 'absolute', right: 34, bottom: 18, fontSize: 7, letterSpacing: 1, color: LABEL_GRAY }}>{pageNumber}</div>
    </div>
  )
}

function ClosingSlide({ colors, orgName }: { colors: Colors; orgName: string }) {
  return (
    <div
      style={{
        width: SLIDE_W,
        height: SLIDE_H,
        background: colors.primary,
        fontFamily: SERIF,
        textAlign: 'left',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Full-bleed gold top bar */}
      <div style={{ position: 'absolute', left: 0, top: 0, width: '100%', height: 6, background: colors.secondary }} />

      <div style={{ position: 'absolute', left: 55, top: 168, fontSize: 21, fontStyle: 'italic', color: colors.secondary }}>
        Business Advisory Report
      </div>
      <div style={{ position: 'absolute', left: 53, top: 200, fontSize: 88, fontWeight: 700, lineHeight: 1, color: CREAM }}>Thank you</div>
      <div style={{ position: 'absolute', left: 55, top: 312, width: 48, height: 3, background: colors.secondary }} />

      <div
        style={{
          position: 'absolute',
          left: 55,
          top: 322,
          fontFamily: SANS,
          fontSize: 15,
          letterSpacing: 2,
          textTransform: 'uppercase',
          color: 'rgba(255,255,255,0.55)',
        }}
      >
        Prepared using <span style={{ fontWeight: 700, color: CREAM }}>Dashboard Insights</span>
      </div>

      <div
        style={{
          position: 'absolute',
          left: 55,
          bottom: 30,
          fontFamily: SANS,
          fontSize: 7,
          letterSpacing: 1.2,
          textTransform: 'uppercase',
          color: 'rgba(255,255,255,0.5)',
        }}
      >
        {orgName} · Business Advisory Report
      </div>
    </div>
  )
}

function ScaledSlide({ width, children }: { width: number; children: ReactNode }) {
  const scale = width / SLIDE_W
  const height = SLIDE_H * scale
  return (
    <div
      style={{
        width,
        height,
        overflow: 'hidden',
        background: '#fff',
        boxShadow: '0 1px 4px rgba(0, 0, 0, 0.12)',
        border: '1px solid #E5E7EB',
        flexShrink: 0,
      }}
    >
      <div
        style={{
          width: SLIDE_W,
          height: SLIDE_H,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
        }}
      >
        {children}
      </div>
    </div>
  )
}

export function PptReportPreview({
  form,
  clientName,
  orgName,
}: {
  form: FormInstance<ReportFormValues>
  clientName?: string
  orgName?: string
}) {
  const [open, setOpen] = useState(false)
  const primary = (Form.useWatch('primaryColor', form) || '').trim() || DEFAULT_PRIMARY
  const secondary = (Form.useWatch('secondaryColor', form) || '').trim() || DEFAULT_SECONDARY
  const text = (Form.useWatch('textColor', form) || '').trim() || DEFAULT_CLIENT_TEXT_COLOR
  const colors: Colors = { primary, secondary, text }
  const resolvedClientName = clientName?.trim() || FALLBACK_CLIENT_NAME
  const resolvedOrgName = orgName?.trim() || FALLBACK_ORG_NAME

  const slideList: ReactNode[] = [
    <Slide1 colors={colors} clientName={resolvedClientName} orgName={resolvedOrgName} />,
    <SummarySlide colors={colors} clientName={resolvedClientName} orgName={resolvedOrgName} title="Executive Summary" pageNumber="02" />,
    <ClosingSlide colors={colors} orgName={resolvedOrgName} />,
  ]

  return (
    <div className="mt-6">
      <div className="text-sm font-medium text-gray-700 mb-2">PPT preview</div>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex flex-wrap gap-3 p-3 rounded-md border border-gray-200 bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer text-left w-full"
        aria-label="Open enlarged PPT preview"
      >
        {slideList.map((slide, i) => (
          <ScaledSlide key={i} width={215}>
            {slide}
          </ScaledSlide>
        ))}
      </button>
      <div className="text-xs text-gray-500 mt-1.5">Click to enlarge · Title, Executive Summary, and closing slides</div>

      <Modal open={open} onCancel={() => setOpen(false)} footer={null} width={1200} title="PPT preview" centered destroyOnClose>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: '8px 0 4px', alignItems: 'center' }}>
          {slideList.map((slide, i) => (
            <ScaledSlide key={i} width={1100}>
              {slide}
            </ScaledSlide>
          ))}
        </div>
      </Modal>
    </div>
  )
}
